import { Button } from "$components/button/button";
import { makeStyles } from "$components/mui-styles";
import {
  Table,
  type TableColumnDefinition,
  type TableRowDefinition,
} from "$components/table";
import {
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  styled,
  Typography,
} from "@material-ui/core";
import { format, subMonths } from "date-fns";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import {
  type DownloadColumnFilterValues,
  RegisterType,
} from "../../../../__generated__/base.gql";
import { DATE_FORMAT } from "../../_lib/constants";
import { DownloadDateFilters } from "../../_lib/download-date-filters";
import { RegisterDownloadWarning } from "../../_lib/register-download-warning";
import { useRequestDownload } from "../../_lib/use-request-download";
import { linkToRegistersPatient } from "../../links";
import { getDownloadColumns } from "../_lib/columns";

const useStyles = makeStyles(() => ({
  columnTableCell: {
    border: "none",
    padding: "0",
  },
  columnTable: {
    overflowX: "auto",
  },
}));

const ButtonContainer = styled(Box)(() => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
}));

const columnDef: TableColumnDefinition = {
  flex: 1,
  padding: "none",
  field: "none",
};

const tableColumns: TableColumnDefinition[] = [
  { ...columnDef, field: "0" },
  { ...columnDef, field: "1" },
  { ...columnDef, field: "2" },
];

export const PatientRegisterDownload = () => {
  const { formatMessage } = useIntl();
  const styles = useStyles();
  const [startDate, setStartDate] = useState(
    format(subMonths(new Date(), 1), DATE_FORMAT),
  );
  const [endDate, setEndDate] = useState(format(new Date(), DATE_FORMAT));

  const [requestDownload] = useRequestDownload();

  const [selectedColumns, setSelectedColumns] = useState<
    Record<DownloadColumnFilterValues, boolean>
  >(
    getDownloadColumns(formatMessage).reduce(
      (acc, c) => ({
        ...acc,
        [c.field]: false,
      }),
      {} as Record<DownloadColumnFilterValues, boolean>,
    ),
  );

  const selectedAttributes = React.useMemo<DownloadColumnFilterValues[]>(() => {
    return Object.entries(selectedColumns)
      .map(([name, selected]) => (selected ? name : undefined))
      .filter(Boolean) as DownloadColumnFilterValues[];
  }, [selectedColumns]);

  const columnClasses = {
    rowCellClass: () => styles.columnTableCell,
    root: styles.columnTable,
  };

  const onColumnToggle = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setSelectedColumns((prevSelected) => {
        return {
          ...prevSelected,
          [e.target.name]: checked,
        };
      });
    },
    [],
  );

  const tableColumnsRows: TableRowDefinition[] = React.useMemo(
    () =>
      Object.entries(
        getDownloadColumns(formatMessage).reduce(
          (acc, c, idx) => {
            const rowIdx = (idx % 4).toString();
            const columnIdx =
              Math.ceil((idx / 4) % 4) - (idx % 4 === 0 ? 0 : 1);

            const ColumnCheckbox = () => {
              return (
                <Box>
                  <FormControlLabel
                    label={c.headerName}
                    control={
                      <Checkbox
                        name={c.field}
                        onChange={onColumnToggle}
                        checked={
                          selectedColumns[c.field as DownloadColumnFilterValues]
                        }
                      />
                    }
                  />
                </Box>
              );
            };

            const existing = acc[rowIdx] ? acc[rowIdx] : {};
            acc[rowIdx] = { ...existing, [columnIdx]: <ColumnCheckbox /> };

            return acc;
          },
          {} as Record<string, Record<number, JSX.Element>>,
        ),
      ).map(([key, value]) => ({ ...value, id: key })),
    [formatMessage, onColumnToggle, selectedColumns],
  );
  return (
    <Container>
      <Box my={2}>
        <Typography variant="h3">
          <FormattedMessage
            id="register.download.title"
            defaultMessage="Download Patient Register"
          />
        </Typography>
      </Box>
      <Box>
        <RegisterDownloadWarning />
        <DownloadDateFilters
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        <Box>
          <Typography variant="subtitle2">
            <FormattedMessage
              id="register.download.columns.header"
              defaultMessage="Select columns to add:"
            />
          </Typography>
          <Table
            classes={columnClasses}
            hover={false}
            hideHeader
            columns={tableColumns}
            rows={tableColumnsRows}
          />
        </Box>
      </Box>

      <ButtonContainer>
        <Box>
          <Link color="secondary" to={linkToRegistersPatient()}>
            <Button color="secondary">
              <FormattedMessage
                id="register.download.cancel"
                defaultMessage="Cancel"
              />
            </Button>
          </Link>
        </Box>
        <Button
          color="primary"
          disabled={selectedAttributes.length === 0}
          onClick={() => {
            requestDownload({
              variables: {
                input: {
                  registerType: RegisterType.GenericOpd,
                  filterOptions: { startDate, endDate },
                  columnFilter: selectedAttributes,
                },
              },
            });
          }}
        >
          <FormattedMessage
            id="register.download.submit"
            defaultMessage="Download"
          />
        </Button>
      </ButtonContainer>
    </Container>
  );
};
